import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';

import firebase from 'firebase/compat/app';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, delay, filter, switchMap, take } from 'rxjs/operators';
import { ErrorService } from 'src/app/core/error/error.service';

import * as moment from 'moment';
import { environment } from '../../../../../environments/environment';
import { LocalStorageService } from '../../../localStorage/local-storage.service';
import { AuditLogProductCodes } from '../../../services/audit-log/auditLog.model';
import { AuthAuditLogService } from '../../../services/audit-log/auth-audit-log.service';
import { UserLaneService } from '../../user-lane/services/user-lane.service';
import { Customer, CustomerService } from './customer.service';

export interface AppUser {
  displayName: string;
  email: string;
  photoURL: string;
  gId: string;
  profile?: any;
  lastAuthentication?: Date;
  customer: {
    customerId: string;
  };
  encodedUser?: string; // Analytics SYNC-989
  role: 'admin' | 'teacher';
}

interface UserProfile {
  picture: string;
  name: string;
  id: string;
  hd: string;
  email: string;
}

interface TestUser {
  accessToken: string;
  idToken: string;
  userProfile: {
    email: string;
    email_verified: boolean;
    family_name: string;
    given_name: string;
    hd: string;
    locale: string;
    name: string;
    picture: string;
    sub: string;
  };
}

interface SupportUser {
  access: true;
  lastLogin?: any;
}

const STORAGE_KEY = 'LS_SYNC_USER';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user: firebase.User | null;
  appUser: AppUser;

  customerSettings$: Observable<Customer>;
  customerSettings: Customer;
  appUser$: Observable<AppUser | null>;
  //  SYNC-1123 busy member added to enable spinner in CustomerLoginComponent
  busy = false;

  private userSubject = new ReplaySubject<AppUser | null>(1);
  private loginInProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    public afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private route: ActivatedRoute,
    private router: Router,
    private errorService: ErrorService,
    private customerSvc: CustomerService,
    private localStorage: LocalStorageService,
    private userlaneService: UserLaneService,
    private snackBarService: MatSnackBar,
    private acf: AngularFireFunctions,
    private auditLogService: AuthAuditLogService,
  ) {
    this.appUser$ = this.userSubject.asObservable();
    this.auditLogService.init(this);

    this.router.events
      .pipe(
        debounceTime(1000),
        delay(15000),
        switchMap(() => this.appUser$),
        filter((appUser) => !!appUser),
        filter(() => !this.loginInProgress.value),
        filter((appUser) => this.checkAuthTimeOut(appUser)),
      )
      .subscribe(async (appUser) => {
        console.log('User session timeout');
        await this.logout(appUser.role, true);
      });

    this.afAuth.authState.subscribe(async (user) => {
      let userData;
      if (user) {
        try {
          userData = JSON.parse(this.b64ToUtf8(this.localStorage.get(STORAGE_KEY)));
        } catch (error) {
          // console.log(`Can't read local storage`);
        }

        if (userData) {
          this.user = user;

          this.appUser = userData;
          this.userSubject.next(userData);

          //Check if user has been logged in > 24 hours
          if (this.checkAuthTimeOut(userData)) {
            console.log('Session timed out');
            return await this.logout(userData.role, true);
          }

          try {
            // Check if user has proper token claims and add if missing
            await this.hasClaims(
              {
                customerId: userData.customer.customerId,
                userWorkspaceId: userData.gId,
              },
              true,
            );
          } catch (error) {
            console.error(`Unable to set required claims on user`, error);
            return await this.logout(userData.role, false);
          }
          this.customerSvc.setCustomerId(userData.customer.customerId);
          this.customerSettings$ = this.customerSvc.loadCustomer(userData.customer.customerId);
          this.userlaneService.identify(this.appUser);
          if (
            this.appUser.role === 'teacher' &&
            (this.router.routerState.snapshot.url === '/teacher-review/login' ||
              this.router.routerState.snapshot.url === '/')
          ) {
            this.router.navigate(['/teacher-review']).catch((error) => {
              console.error(error);
            });
          } else if (this.appUser.role === 'admin' && this.router.routerState.snapshot.url === '/') {
            this.router.navigate(['/sync/home']).catch((error) => {
              console.error(error);
            });
          }
        }
      } else {
        this.user = null;
        this.userSubject.next(null);
      }
    });
  }

  getCurrentUserIdToken(): Promise<string> {
    return this.afAuth.currentUser.then(user => user?.getIdToken(true));
  }

  checkAuthTimeOut(userData: AppUser): boolean {
    return (
      !userData.hasOwnProperty('lastAuthentication') ||
      moment().diff(moment(userData.lastAuthentication), 'hours') >= 24
    );
  }

  // Setting custom claims always overrides other custom claims.
  // Need to perform a diff to ensure claims don't get overridden, especially if user logs in from SSO and refreshes.
  // https://firebase.google.com/docs/auth/admin/custom-claims#set_and_validate_custom_user_claims_via_the_admin_sdk
  // https://stackoverflow.com/a/54870651
  async hasClaims(claims: { [key: string]: string }, addMissingClaims = false): Promise<boolean> {
    const lockedClaims = ['aud', 'auth_time', 'exp', 'iat', 'iss', 'sub', 'firebase'];
    const userClaimToken = await this.user.getIdTokenResult();
    const missingClaims = Object.keys(claims).reduce((missingAcc, key) => {
      if (!userClaimToken.claims.hasOwnProperty(key)) {
        missingAcc[key] = claims[key];
      }
      return missingAcc;
    }, {});
    const existingCustomClaims = Object.keys(userClaimToken.claims).reduce((customAcc, key) => {
      if (!lockedClaims.includes(key)) {
        customAcc[key] = userClaimToken.claims[key];
      }
      return customAcc;
    }, {});

    if (Object.keys(missingClaims).length > 0 && addMissingClaims) {
      try {
        await this.acf
          .httpsCallable('sso-addClaimToUser')({
            claims: Object.assign(existingCustomClaims, missingClaims),
          })
          .toPromise();
        //force refresh so claims can be used in this session
        await this.user.getIdTokenResult(true);
      } catch (error) {
        console.error('Unable to add custom token claims');
      }
    } else if (Object.keys(missingClaims).length > 0) {
      return false;
    } else if (Object.keys(missingClaims).length === 0) {
      return true;
    }
  }

  async logout(userRole = 'none', audit = true): Promise<boolean> {
    console.log('userRole', userRole, this.appUser);
    if (userRole === 'none' && this.appUser) {
      userRole = this.appUser.role;
    }
    if (audit) {
      await this.auditLogService.addLogEntry({
        productCode:
          userRole === 'teacher' ? AuditLogProductCodes.TEACHER_REVIEW : AuditLogProductCodes.LITTLE_SIS_PREMIUM,
        actionId: 1,
        typeId: 1,
        logData: { message: `${this.appUser.displayName} logged out` },
      });
    }

    // This removes a user from the email cache RENAME THIS.
    // It DOES NOT delete the current user.
    // TODO: This is way too slow and makes the logout look broken.
    // We need to change this to pass in the user to remove from the cache
    // And call it AFTER the signOut is done. That mean we will need to remove
    // any data taken from the context in the CF as it will not be there anymore

    await this.acf.httpsCallable('users-deleteCurrentUser')({}).toPromise();

    return this.afAuth.signOut().then(async () => {
      this.user = null;
      this.loginInProgress.next(false);
      this.appUser = null;
      this.localStorage.remove(STORAGE_KEY);
      this.userlaneService.hide();
      if (userRole === 'teacher') {
        await this.router.navigate(['/teacher-review/login']);
      } else {
        await this.router.navigate(['/']);
      }
      return true;
    });
  }

  async login(
    role: 'teacher' | 'admin',
    support = false,
    testLogin = false,
    followOnPath = '/sync/home',
    queryParams: Params = {},
  ): Promise<{ success: boolean; message: string } | boolean> {
    // This needs some changes. Some users should not be able to log in at all, but right now the main log in will let them authenticate
    this.loginInProgress.next(true);
    if (role !== 'teacher' && role !== 'admin') {
      this.loginInProgress.next(false);
      throw new Error(`Invalid role "${role}" provided.`);
    }

    await this.afAuth.setPersistence('local');

    let userObj: firebase.auth.UserCredential;

    try {
      if (testLogin) {
        const testUser: TestUser = JSON.parse(this.localStorage.get('testUser'));
        this.localStorage.remove('testUser');
        const credential = firebase.auth.GoogleAuthProvider.credential(testUser.idToken, testUser.accessToken);
        userObj = await this.afAuth.signInWithCredential(credential);
        userObj.additionalUserInfo.profile = {
          email: testUser.userProfile.email,
          picture: testUser.userProfile.picture,
          name: testUser.userProfile.name,
          id: testUser.userProfile.sub,
          hd: testUser.userProfile.hd,
          ...userObj.additionalUserInfo.profile,
        };
        console.log('userObj', userObj.additionalUserInfo);
      } else {
        const provider = new firebase.auth.GoogleAuthProvider();
        userObj = await this.afAuth.signInWithPopup(provider);
      }
      this.busy = true;
      this.user = userObj.user;
      if (this.user?.email && !this.user?.providerData.find((data) => data.email === this.user.email)) {
        // This user is cached in firebase with an email address that is no longer attached to the account at all, let's update it
        await this.logout(role, false);
        this.busy = false;
        this.snackBarService.open('Refreshing your account... Please log in again.', '', {
          duration: 10000,
          panelClass: ['errorSnack'],
        });
        return;
      }
    } catch (error) {
      await this.logout(role, false);
    }

    const userProfile: UserProfile = userObj.additionalUserInfo.profile as UserProfile;

    // Check to see if the support flag is set and if so check against list of support login accounts
    let found: SupportUser;

    if (support) {
      try {
        found = (await (
          await this.afs.doc(`app/support/users/${userProfile.email}`).get().toPromise()
        ).data()) as SupportUser;
      } catch (error) {
        found = undefined;
      }

      if (!found || !found.access) {
        console.warn(`No access - go to ${userProfile.email}`);
        this.snackBarService.open(
          `Unable to access Little SIS. Please go to ${environment.urls.external.littleSisWeb} or contact your admin for support.`,
          '',
          {
            duration: 10000,
            panelClass: ['errorSnack'],
          },
        );

        setTimeout(async () => {
          await this.logout(role, false);
        }, 10000);
        return;
      }
    }

    let customer;
    // Check for customerId and log out if not found...
    try {
      customer = (await this.afs.doc<Customer>(`domains/${userProfile.hd}`).get().toPromise()).data() as Customer;
      if (!customer || !customer.customerId) {
        throw new Error('');
      }
      const customerDoc = (await this.afs.doc<Customer>(`customers/${customer.customerId}`).get().toPromise()).data() as Customer;
      if (!customerDoc || !customerDoc.customerId || !customerDoc.domain) {
        throw new Error('');
      }
    } catch (error) {
      console.error(`Cannot get customer record :: ${JSON.stringify(userProfile)}`);
      await this.logout(role, false);
      this.snackBarService.open(
        'Unable to find your Google Workspace domain. Please try logging in through Little SIS for Classroom or contact your admin for support.',
        '',
        { duration: 10000, panelClass: ['errorSnack'] },
      );
      return;
    }

    // Check custom claims and add if missing
    try {
      await this.hasClaims(
        {
          customerId: customer.customerId,
          userWorkspaceId: userProfile.id,
        },
        true,
      );
    } catch (error) {
      console.error(`Unable to set required claims on user`, error);
      return await this.logout(role, false);
    }

    this.customerSvc.setCustomerId(customer.customerId);
    this.customerSettings$ = this.customerSvc.loadCustomer(customer.customerId);
    this.customerSettings = await this.customerSettings$.pipe(take(1)).toPromise();

    if (role === 'teacher') {
      // Check the user has been granted access to review by checking for the existence of the firebase teacher-review node
      try {
        const teacherReviewNode = (
          await this.afs.doc(`customers/${customer.customerId}/teacherReview/${userProfile.id}`).get().toPromise()
        ).data();
        if (!teacherReviewNode) {
          throw new Error('User has not been configured for teacher review');
        }
      } catch (error) {
        console.error('User has not been configured for teacher review');
        await this.logout(role, false);
        this.snackBarService.open(
          'You are not currently set up for Teacher Review. Please contact your admin for support.',
          '',
          { duration: 10000, panelClass: ['errorSnack'] },
        );
        return {
          success: false,
          message: 'Teacher not configured for teacher review',
        };
      }
    }

    const appUserData = {
      displayName: userProfile.name,
      email: userProfile.email,
      photoURL: userProfile.picture,
      gId: userProfile.id,
      lastAuthentication: new Date(),
      customer: {
        customerId: customer.customerId,
      },
      role,
    };

    // TODO: This really slows down the login.
    // We need to add a spinner or status on the login screen
    // Right now it can look broken waiting for a cold start.

    const lscCall = this.acf.httpsCallable('sso-getProfile');
    const response: any = await lscCall({
      gId: userProfile.id,
      role,
    }).toPromise();
    // This logic only applies to admin login, not teacher review login because teachers may not be set up in lsc
    if (role === 'admin') {
      if (response.profile?.isDisabled !== 0) {
        console.error(`User ${response.profile?.isDisabled ? 'is disabled' : 'does not exist'}`);
        await this.logout(role, false);
        this.snackBarService.open(
          'It looks like you do not have an active Little SIS account. Please contact your Google Workspace admin to request access.',
          '',
          { duration: 10000, panelClass: ['errorSnack'] },
        );
        return;
      }
      if (response.profile?.hasLspPermission !== 1) {
        console.error('User does not have Little SIS Premium permission in their current role');
        await this.logout(role, false);
        this.snackBarService.open(
          'It looks like you do not have access to Little SIS Premium. Please contact your Google Workspace admin to request access.',
          '',
          { duration: 10000, panelClass: ['errorSnack'] },
        );
        return;
      }
    }
    if (response.success) {
      Object.assign(appUserData, { profile: response.profile });
    }
    this.appUser = appUserData;
    this.userSubject.next(this.appUser);
    this.localStorage.set(STORAGE_KEY, this.utf8ToB64(JSON.stringify(this.appUser)));
    this.userlaneService.identify(this.appUser);

    if (this.appUser.role === 'admin') {
      await this.auditLogService.addLogEntry({
        productCode: AuditLogProductCodes.LITTLE_SIS_PREMIUM,
        actionId: 2,
        typeId: 1,
        logData: { success: true, message: 'Logged in via local admin' },
      });
    } else if (this.appUser.role === 'teacher') {
      await this.auditLogService.addLogEntry({
        productCode: AuditLogProductCodes.TEACHER_REVIEW,
        actionId: 2,
        typeId: 1,
        logData: { success: true },
      });
    }

    if (this.appUser.role === 'teacher') {
      this.router.navigate(['/teacher-review'], { queryParams: { welcome: true } }).catch((error) => {
        console.error(error);
      });
    } else {
      this.router.navigate([followOnPath], { queryParams }).catch((error) => {
        console.error(error);
      });
    }
    this.busy = false;
    this.loginInProgress.next(false);
  }

  async ssoLogin(ssoObj: { token: string }, followOnPath = '/sync/home', queryParams: Params = {}): Promise<void> {
    this.loginInProgress.next(true);
    await this.afAuth.setPersistence('local');
    const userObj = await this.afAuth.signInWithCustomToken(ssoObj.token);
    const resp = await userObj.user.getIdTokenResult();

    this.customerSvc.setCustomerId(resp.claims.customerId); //already using claims
    this.customerSettings$ = this.customerSvc.loadCustomer(resp.claims.customerId);
    this.customerSettings = await this.customerSettings$.pipe(take(1)).toPromise();
    this.appUser = {
      displayName: resp.claims.profile.displayName,
      email: resp.claims.profile.primaryEmail,
      photoURL: resp.claims.profile.photoURL,
      gId: resp.claims.profile.id,
      profile: resp.claims.profile,
      lastAuthentication: new Date(),
      customer: {
        customerId: resp.claims.customerId,
      },
      role: 'admin',
    };

    this.userSubject.next(this.appUser);
    this.localStorage.set(STORAGE_KEY, this.utf8ToB64(JSON.stringify(this.appUser)));
    this.userlaneService.identify(this.appUser);

    let tokenResult: firebase.auth.IdTokenResult;
    try {
      tokenResult = await userObj.user.getIdTokenResult();
    } catch (error) {
      console.error(error);
      await this.logout('admin', false);
    }
    this.customerSvc.setCustomerId(tokenResult.claims.customerId); //already using claims
    this.customerSettings$ = this.customerSvc.loadCustomer(tokenResult.claims.customerId);
    this.customerSettings = await this.customerSettings$.pipe(take(1)).toPromise();
    this.appUser = {
      displayName: tokenResult.claims.profile.displayName,
      email: tokenResult.claims.profile.primaryEmail,
      photoURL: tokenResult.claims.profile.photoURL,
      gId: tokenResult.claims.profile.id,
      profile: tokenResult.claims.profile,
      lastAuthentication: new Date(),
      customer: {
        customerId: tokenResult.claims.customerId,
      },
      role: 'admin',
    };

    this.userSubject.next(this.appUser);
    this.localStorage.set(STORAGE_KEY, window.btoa(JSON.stringify(this.appUser)));
    this.userlaneService.identify(this.appUser);

    await this.auditLogService.addLogEntry({
      productCode: AuditLogProductCodes.LITTLE_SIS_PREMIUM,
      actionId: 2,
      typeId: 1,
      logData: { success: true, message: 'Logged in via sso' },
    });

    await this.router.navigate([followOnPath], {
      queryParams: {
        q: null,
        preAuth: null,
        ...queryParams,
      },
      queryParamsHandling: 'merge',
    });
    this.loginInProgress.next(false);
  }

  utf8ToB64(str: string): string {
    return window.btoa(unescape(encodeURIComponent(str)));
  }

  b64ToUtf8(str: string): string {
    return decodeURIComponent(escape(window.atob(str)));
  }
}
